import { AuditLogEntity } from "domain/entity/AuditLog/AuditLogEntity";
import { AuditLogSearchCriteria, EMPTY_AUDIT_LOG_SEARCH_CRITERIA } from "domain/entity/AuditLog/AuditLogSearchCriteria";
import { MasterDataType } from "domain/entity/MasterData/MasterDataEntity";
import { AuditLogRepository } from "domain/repository/AuditLog/AuditLogRepo";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import { OpsLineRepository } from "domain/repository/OpsLine/OpsLineRepo";
import { UserRepository } from "domain/repository/User/UserRepo";
import _ from "lodash";
import { AuditLogMaintenanceModel } from "presentation/model/AuditLog/AuditLogMaintenanceModel";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface AuditLogMaintenanceVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<AuditLogMaintenanceModel>> | ((value: SetStateAction<AuditLogMaintenanceModel>) => void),
    ],
    repo: AuditLogRepository,
    opsLineRepo: OpsLineRepository,
    masterDataRepo: MasterDataRepository,
    userRepo: UserRepository,
}

export const AuditLogMaintenanceVM = ({ dispatch, repo, opsLineRepo, masterDataRepo, userRepo }: AuditLogMaintenanceVMProps) => {
    const [auditLogMainDispatch] = dispatch;

    const loadDropdownOption = async () => {

        await userRepo.getAllUsers().then(users => {
            let newUsers = _.orderBy(users, ["name"]);
            const loginUserDropdownOption = newUsers?.map((user) => ({
                dropdownLabel: user.name,
                tagLabel: user.name,
                value: user.name,
            })) ?? []

            auditLogMainDispatch(prevState => ({
                ...prevState,
                dynamicOptions: {
                    ...prevState.dynamicOptions,
                    userDropdownOptions: loginUserDropdownOption,
                }
            }))
        });

        await opsLineRepo.getOpsLineForCombobox().then(
            opsLines => {
                const opsLinesDropdownOptions = opsLines?.map((opsLineEntity) => ({
                    dropdownLabel: opsLineEntity.opsLine,
                    tagLabel: opsLineEntity.opsLine,
                    value: opsLineEntity.opsLine,
                })) ?? []

                auditLogMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        opsLineDropdownOptions: opsLinesDropdownOptions
                    }
                }))
            }
        )

        await masterDataRepo.getMasterDataByKey(MasterDataType.FORWARDER_CODE).then(
            forwarderCodes => {
                const forwarderCodesDropdownOptions = forwarderCodes?.map((forwarderCode) => ({
                    dropdownLabel: forwarderCode.code,
                    tagLabel: forwarderCode.code,
                    value: forwarderCode.code,
                })) ?? []

                auditLogMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        forwarderCodeDropdownOptions: forwarderCodesDropdownOptions,
                    }
                }))
            }
        )
    }
    const onSearchClick = async () => {
        auditLogMainDispatch(prevState => {
            return {
                ...prevState,
                isShowSearchPanel: !prevState.isShowSearchPanel
            }
        });
    }

    const onCloseDetailPanel = async () => {
        auditLogMainDispatch(prevState => {
            return {
                ...prevState,
                isShowDetailPanel: false
            }
        });
    }

    const onRemoveAllSearchCriteria = async () => {
        auditLogMainDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: { ...EMPTY_AUDIT_LOG_SEARCH_CRITERIA }
            }
        });
    }
    const onSearch = async (criteria: AuditLogSearchCriteria) => {
        auditLogMainDispatch(prevState => {
            return {
                ...prevState,
                // auditLogList: [],
                searchCriteria: criteria,
            }
        });
        await repo.getEntities(criteria).then((data) => {
            auditLogMainDispatch(prevState => {
                return {
                    ...prevState,
                    auditLogList: data,
                    searchCriteria: criteria,
                }
            });
        });
    }

    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }
        if ('auditLogType' === fieldKey) {
            auditLogMainDispatch(prevState => {
                return {
                    ...prevState,
                    auditLogList: val !== prevState.searchCriteria.auditLogType ? [] : prevState.auditLogList
                }
            })
        }



        auditLogMainDispatch(prevState => {
            if ('proposalNo' === fieldKey && val) {
                const regex = /^\d*$/;
                if (val && !regex.test(val)) {
                    val = prevState.searchCriteria[fieldKey];
                }
            }

            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: val,
                },
            }
        })
    }

    const onDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        auditLogMainDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            }
        }))
    }

    const onTextAreaChange = (e: ChangeEvent<HTMLTextAreaElement>, fieldName: string, toUpperCase: boolean = false) => {
        const val = toUpperCase ? e.target.value.toUpperCase() : e.target.value;

        let cntrNoArray: string[] = [];
        if (val && val.trim() !== '') {
            cntrNoArray = val.split('\n');
        }

        auditLogMainDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [fieldName]: fieldName === 'cntrNos' ?
                    cntrNoArray ?? [] :
                    val === "" ? null : val,
            }
        }));
    };

    const onRowDoubleClick = async (entity: AuditLogEntity) => {
        auditLogMainDispatch(prevState => ({
            ...prevState,
            currentEntity: entity,
            isShowDetailPanel: true,
        }))
    }

    const setIsSearchLoading = (isSearchLoading: boolean) => {
        auditLogMainDispatch(prevState => {
            return {
                ...prevState,
                isSearchLoading: isSearchLoading,
            }
        })
    }


    return {
        onRowDoubleClick: onRowDoubleClick,
        onSearchClick: onSearchClick,
        onRemoveAllSearchCriteria: onRemoveAllSearchCriteria,
        onSearch: onSearch,
        onSearchFieldChange: onSearchFieldChange,
        onDateRangeChange: onDateRangeChange,
        onTextAreaChange: onTextAreaChange,
        loadDropdownOption: loadDropdownOption,
        onCloseDetailPanel: onCloseDetailPanel,
        setIsSearchLoading: setIsSearchLoading,
    }
}